// src/services/buildings3D.js - 3D здания для MAPI с тёмной цветовой схемой

import { COLORS } from './mapConfig';

/**
 * 🏗️ Создаёт 3D здания для MAPI с автоматическими высотами
 */
export function createMAPIBuildings3D(map) {
    if (!map) {
      console.warn('[Buildings3D] ⚠️ Map is null');
      return false;
    }

  try {
    console.log('[Buildings3D] 🏗️ Creating 3D buildings for MAPI...');

    // 🔍 Проверяем есть ли источник данных зданий
    const sources = map.getStyle().sources;
    const buildingSource = Object.keys(sources).find(key => 
      sources[key].type === 'vector' && 
      (key.includes('mapi') || key.includes('protomaps') || sources[key].tiles)
    );

    if (!buildingSource) {
      console.warn('[Buildings3D] ⚠️ No vector source found for buildings');
      return false;
    }

    console.log(`[Buildings3D] 📦 Using source: ${buildingSource}`);

    // 🎯 Находим куда вставить слой (под символами)
    const layers = map.getStyle().layers;
    const labelLayerId = layers.find(layer => 
      layer.type === 'symbol' && 
      layer.layout && 
      layer.layout['text-field']
    )?.id;

    // 🏢 СОЗДАЁМ 3D ЗДАНИЯ В ТЁМНОЙ ГАММЕ
    const buildingsLayerId = 'mapi-buildings-3d';
    
    map.addLayer({
      id: buildingsLayerId,
      type: 'fill-extrusion',
      source: buildingSource,
      'source-layer': 'buildings', // Protomaps использует 'buildings'
      minzoom: 12, // 🎯 Появляются при зуме 12
      paint: {
        // 🎨 ЦВЕТА В СТИЛЕ ВАШЕЙ ТЁМНОЙ ТЕМЫ
        'fill-extrusion-color': [
          'case',
          // Жилые здания - фиолетово-серый
          ['in', ['to-string', ['get', 'building']], ['literal', ['residential', 'apartments', 'house']]], 
          COLORS.BUILDING_FILL || '#1A1C1F',
          
          // Офисы - синевато-серый
          ['in', ['to-string', ['get', 'building']], ['literal', ['office', 'commercial']]], 
          '#1C1E21',
          
          // Торговые - слегка коричневатый
          ['in', ['to-string', ['get', 'building']], ['literal', ['retail', 'mall', 'shop']]], 
          '#1F1C19',
          
          // По умолчанию - основной цвет зданий
          COLORS.BUILDING_FILL || '#161719'
        ],

        // 🏗️ УМНЫЕ ВЫСОТЫ С АНИМАЦИЕЙ РОСТА
        'fill-extrusion-height': [
          'interpolate',
          ['linear'],
          ['zoom'],
          12, 0, // При зуме 12 - высота 0 (здания скрыты)
          12.5, [ // При зуме 12.5 - начинают "расти"
            'case',
            // Если есть реальная высота - используем её
            ['all', ['has', 'height'], ['>', ['to-number', ['get', 'height']], 0]], 
            ['to-number', ['get', 'height']],
            
            // Если есть этажность - считаем высоту
            ['all', ['has', 'levels'], ['>', ['to-number', ['get', 'levels']], 0]], 
            ['*', ['to-number', ['get', 'levels']], 3.5],
            
            // 🎲 АВТОМАТИЧЕСКИЕ ВЫСОТЫ ПО ТИПАМ ЗДАНИЙ
            [
              'case',
              // Жилые дома - 2-8 этажей (7-28м)
              ['in', ['to-string', ['get', 'building']], ['literal', ['residential', 'apartments', 'house']]], 
              ['+', 7, ['*', ['%', ['coalesce', ['to-number', ['get', 'osm_id']], ['to-number', ['get', 'id']], 12345], 22], 1]],
              
              // Офисы - 3-15 этажей (10-52м) 
              ['in', ['to-string', ['get', 'building']], ['literal', ['office', 'commercial']]], 
              ['+', 10, ['*', ['%', ['coalesce', ['to-number', ['get', 'osm_id']], ['to-number', ['get', 'id']], 23456], 43], 1]],
              
              // Торговые центры - 1-4 этажа (4-16м)
              ['in', ['to-string', ['get', 'building']], ['literal', ['retail', 'mall', 'shop']]], 
              ['+', 4, ['*', ['%', ['coalesce', ['to-number', ['get', 'osm_id']], ['to-number', ['get', 'id']], 34567], 13], 1]],
              
              // Промышленные - 1-3 этажа (5-15м)
              ['in', ['to-string', ['get', 'building']], ['literal', ['industrial', 'warehouse', 'factory']]], 
              ['+', 5, ['*', ['%', ['coalesce', ['to-number', ['get', 'osm_id']], ['to-number', ['get', 'id']], 45678], 11], 1]],
              
              // Обычные здания - 2-6 этажей (6-21м)
              ['+', 6, ['*', ['%', ['coalesce', ['to-number', ['get', 'osm_id']], ['to-number', ['get', 'id']], 56789], 16], 1]]
            ]
          ]
        ],

        // 🏠 База здания (для зданий на возвышениях)
        'fill-extrusion-base': [
          'interpolate',
          ['linear'],
          ['zoom'],
          12, 0,
          12.5, [
            'case',
            ['has', 'min_height'], ['to-number', ['get', 'min_height']],
            0
          ]
        ],

        // 🌫️ Прозрачность и градиент
        'fill-extrusion-opacity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          12, 0.3,   // При появлении - слегка прозрачные
          14, 0.85,  // При детальном зуме - почти непрозрачные
          18, 0.95   // При максимальном зуме - почти полностью непрозрачные
        ],

        // 🌅 Вертикальный градиент для реализма
        'fill-extrusion-vertical-gradient': true
      }
    }, labelLayerId);

    console.log('[Buildings3D] ✅ 3D buildings layer added successfully!');
    
    // 🎮 Добавляем обработчик клика для отладки
    addBuildingClickHandler(map, buildingsLayerId);
    
    return buildingsLayerId;

  } catch (error) {
    console.error('[Buildings3D] ❌ Error creating 3D buildings:', error);
    return false;
  }
}

/**
 * 🔍 Добавляет обработчик клика для отладки зданий
 */
function addBuildingClickHandler(map, layerId) {
  map.on('click', layerId, (e) => {
    if (e.features && e.features[0]) {
      const props = e.features[0].properties;
      console.log('🏗️ BUILDING CLICKED:', {
        building: props.building || 'unknown',
        height: props.height || 'auto-generated',
        levels: props.levels || 'none',
        osm_id: props.osm_id,
        coordinates: e.lngLat
      });
    }
  });

  // Меняем курсор при наведении
  map.on('mouseenter', layerId, () => {
    map.getCanvas().style.cursor = 'pointer';
  });

  map.on('mouseleave', layerId, () => {
    map.getCanvas().style.cursor = '';
  });
}

/**
 * 🎨 Применяет альтернативные цвета зданий
 */
export function applyAlternativeBuildingColors(map, layerId) {
  if (!map.getLayer(layerId)) return false;

  try {
    // 🌙 Ночные цвета
    const nightColors = [
      'case',
      ['in', ['to-string', ['get', 'building']], ['literal', ['residential', 'apartments', 'house']]], 
      '#2A2D3A', // Тёмно-синий для жилых
      
      ['in', ['to-string', ['get', 'building']], ['literal', ['office', 'commercial']]], 
      '#1F2937', // Тёмно-серый для офисов
      
      ['in', ['to-string', ['get', 'building']], ['literal', ['retail', 'mall', 'shop']]], 
      '#374151', // Более светлый для торговых
      
      '#1F2937' // По умолчанию
    ];

    map.setPaintProperty(layerId, 'fill-extrusion-color', nightColors);
    console.log('[Buildings3D] 🌙 Applied night color scheme');
    return true;

  } catch (error) {
    console.error('[Buildings3D] ❌ Error applying colors:', error);
    return false;
  }
}

/**
 * 🕹️ Управление видимостью 3D зданий
 */
export function toggleBuildings3DVisibility(map, layerId) {
  if (!map.getLayer(layerId)) return false;

  try {
    const currentVisibility = map.getLayoutProperty(layerId, 'visibility');
    const newVisibility = currentVisibility === 'none' ? 'visible' : 'none';
    
    map.setLayoutProperty(layerId, 'visibility', newVisibility);
    console.log(`[Buildings3D] 🔄 Visibility toggled to: ${newVisibility}`);
    
    return newVisibility === 'visible';
  } catch (error) {
    console.error('[Buildings3D] ❌ Error toggling visibility:', error);
    return false;
  }
}

/**
 * 🌅 Изменение освещения по времени суток
 */
export function applyTimeBasedLighting(map, layerId, hour = 12) {
  if (!map.getLayer(layerId)) return false;

  try {
    let opacity = 0.85;
    
    if (hour >= 6 && hour < 10) {
      opacity = 0.75; // Утро - немного темнее
    } else if (hour >= 18 && hour < 22) {
      opacity = 0.9;  // Вечер - ярче
    } else if (hour >= 22 || hour < 6) {
      opacity = 0.6;  // Ночь - тёмно
    }

    map.setPaintProperty(layerId, 'fill-extrusion-opacity', opacity);
    console.log(`[Buildings3D] 🌅 Applied lighting for hour ${hour}, opacity: ${opacity}`);
    
    return true;
  } catch (error) {
    console.error('[Buildings3D] ❌ Error applying lighting:', error);
    return false;
  }
}

/**
 * 🔧 Проверяет совместимость и диагностирует проблемы
 */
export function diagnoseBuildings3D(map) {
  if (!map) {
    console.warn('[Buildings3D] ⚠️ Map is null');
    return;
  }

  try {
    console.log('[Buildings3D] 🔍 Diagnostic report:');
    
    const style = map.getStyle();
    console.log('📦 Available sources:', Object.keys(style.sources || {}));
    
    const vectorSources = Object.entries(style.sources || {})
      .filter(([key, source]) => source.type === 'vector');
    console.log('🗺️ Vector sources:', vectorSources.map(([key]) => key));
    
    const buildingLayers = style.layers?.filter(layer => 
      layer.type === 'fill-extrusion' || 
      layer.id?.includes('building') ||
      layer.id?.includes('3d')
    ) || [];
    console.log('🏗️ Building layers:', buildingLayers.map(l => l.id));
    
    console.log('✅ Diagnostic complete');
    
  } catch (error) {
    console.error('[Buildings3D] ❌ Diagnostic error:', error);
  }
}

// 🚀 Основной экспорт
export default {
  createMAPIBuildings3D,
  applyAlternativeBuildingColors,
  toggleBuildings3DVisibility,
  applyTimeBasedLighting,
  diagnoseBuildings3D
};